import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, ArrowUpRight, Send, Sparkles } from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-[#26282C] text-[#A8ADA5]",
  sent: "bg-[#7CFF9B]/15 text-[#7CFF9B]",
  pending: "bg-[#D5FF3F]/15 text-[#D5FF3F]",
  resolved: "bg-[#D5FF3F] text-[#0B0C0E]",
  rejected: "bg-[#FF6B4A]/15 text-[#FF6B4A]",
};

export default function Disputes() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/disputes");
      setDisputes(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Count drafts per bureau for bulk-send strip
  const draftsByBureau = disputes.reduce((acc, d) => {
    if (d.status === "draft" && ["equifax", "experian", "transunion"].includes(d.bureau)) {
      acc[d.bureau] = (acc[d.bureau] || 0) + 1;
    }
    return acc;
  }, {});
  const totalDrafts = Object.values(draftsByBureau).reduce((s, n) => s + n, 0);

  const runBulkSend = async (bureau) => {
    const count = bureau === "all" ? totalDrafts : (draftsByBureau[bureau] || 0);
    if (count === 0) return;
    // Load user's saved mailing address if any
    let addr = {};
    try {
      const stored = JSON.parse(localStorage.getItem("aura_mail_addr") || "{}");
      addr = stored;
    } catch { /* empty */ }

    if (!addr.consumer_address_line1) {
      toast.error("Send one dispute manually first so Aura can save your return address.");
      return;
    }
    const totalCost = (count * 12).toFixed(2);
    const label =
      bureau === "all"
        ? `all ${count} draft letter${count === 1 ? "" : "s"} across every bureau`
        : `${count} ${bureau.charAt(0).toUpperCase() + bureau.slice(1)} letter${count === 1 ? "" : "s"}`;
    if (!window.confirm(
      `Mail ${label} as USPS Certified? You'll be redirected to Stripe to pay $${totalCost} ($12 × ${count} letter${count === 1 ? "" : "s"}). Letters mail automatically after payment.`
    )) return;

    setBulkLoading(bureau);
    try {
      const { data } = await api.post("/mail/bulk-send", {
        bureau,
        consumer_address_line1: addr.consumer_address_line1,
        consumer_address_line2: addr.consumer_address_line2 || null,
        consumer_city: addr.consumer_city,
        consumer_state: addr.consumer_state,
        consumer_zip: addr.consumer_zip,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        toast.success(`Redirecting to checkout — $${((data.total_cents || 0) / 100).toFixed(2)} for ${data.letter_count} letter${data.letter_count === 1 ? "" : "s"}`);
        window.location.href = data.checkout_url;
        return;
      }
      toast.warning("Nothing to send.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkLoading(null);
    }
  };

  return (
    <AppShell>
      <div className="mb-10">
        <div className="overline mb-3">Dispute tracker</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">Every letter, every status.</h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl">
          Draft → sent → pending → resolved. Every letter to Equifax, Experian, and TransUnion.
        </p>
      </div>

      {loading ? (
        <div className="text-[#A8ADA5]">Loading…</div>
      ) : disputes.length === 0 ? (
        <div className="card-surface p-16 text-center">
          <FileText size={26} className="mx-auto text-[#6D716C] mb-4" strokeWidth={1.5} />
          <div className="font-display text-3xl mb-2">No disputes yet.</div>
          <p className="text-[#A8ADA5] mb-8">Generate your first letter from a negative item.</p>
          <Link to="/app/items">
            <Button className="rounded-full btn-lime">Go to items</Button>
          </Link>
        </div>
      ) : (
        <>
          {/* Send-everything callout — the FCRA response clock only starts once letters actually go out. */}
          {Object.keys(draftsByBureau).length > 0 && (
            <div
              className="mb-6 rounded-lg border border-[#D5FF3F]/40 bg-gradient-to-br from-[#D5FF3F]/12 to-transparent p-5 md:p-6"
              data-testid="mail-drafts-callout"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-sm bg-[#D5FF3F] flex items-center justify-center flex-shrink-0">
                  <Send size={18} className="text-[#0B0C0E]" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F] mb-1">
                    Don&rsquo;t stop at &ldquo;drafted&rdquo;
                  </div>
                  <div className="font-display text-xl md:text-2xl leading-tight text-[#F5F3EE] mb-1.5">
                    You have {totalDrafts} letter{totalDrafts === 1 ? "" : "s"} sitting on your desk.
                  </div>
                  <p className="text-sm text-[#A8ADA5] leading-relaxed max-w-2xl">
                    The FCRA §1681i 30-day response clock only starts once the bureaus actually receive
                    your dispute. Mail them USPS Certified with tracking so they <span className="text-[#F5F3EE]">have to</span> respond.
                  </p>
                </div>
              </div>

              {/* Big lime "Send everything" primary + per-bureau outlined chips */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => runBulkSend("all")}
                  disabled={!!bulkLoading}
                  data-testid="bulk-send-all"
                  className="rounded-full btn-lime h-11 px-5 font-bold"
                >
                  {bulkLoading === "all" ? (
                    <>
                      <Sparkles size={14} className="mr-1.5 animate-pulse" />
                      Mailing all {totalDrafts}…
                    </>
                  ) : (
                    <>
                      <Send size={14} className="mr-2" />
                      Send all {totalDrafts} now · ${(totalDrafts * 12).toFixed(2)}
                    </>
                  )}
                </Button>
                {["equifax", "experian", "transunion"].map((b) => {
                  const count = draftsByBureau[b] || 0;
                  if (count === 0) return null;
                  const busy = bulkLoading === b;
                  return (
                    <Button
                      key={b}
                      onClick={() => runBulkSend(b)}
                      disabled={!!bulkLoading}
                      data-testid={`bulk-send-${b}`}
                      className="rounded-full btn-outline h-11 px-4 hover:!border-[#D5FF3F] hover:!text-[#D5FF3F]"
                    >
                      {busy ? (
                        <>
                          <Sparkles size={13} className="mr-1.5 animate-pulse text-[#D5FF3F]" />
                          Mailing {count} to {b.charAt(0).toUpperCase() + b.slice(1)}…
                        </>
                      ) : (
                        <>
                          {b.charAt(0).toUpperCase() + b.slice(1)} only ({count})
                        </>
                      )}
                    </Button>
                  );
                })}
              </div>
              <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">
                $12 per letter · Real USPS Certified Mail w/ Return Receipt · Tracking on every send
              </div>
            </div>
          )}

          <div className="space-y-3" data-testid="disputes-list">
            {disputes.map((d) => (
              <Link
                key={d.id}
                to={`/app/disputes/${d.id}`}
                data-testid={`dispute-row-${d.id}`}
                className="block card-surface p-5 lift"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1.5">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest ${STATUS_STYLES[d.status] || ""}`}
                      >
                        {d.status}
                      </span>
                      <span className="text-xs text-[#6D716C] font-mono uppercase tracking-wider">
                        {d.bureau} · {d.strategy?.replace("_", " ")}
                      </span>
                    </div>
                    <div className="font-medium text-[#F5F3EE] truncate">
                      {d.creditor_snapshot || "Dispute"}{" "}
                      <span className="text-[#6D716C] text-sm capitalize font-normal">
                        · {(d.item_type_snapshot || "").replace("_", " ")}
                      </span>
                    </div>
                    <div className="text-xs text-[#6D716C] mt-1 font-mono">
                      Drafted {new Date(d.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <ArrowUpRight size={16} className="text-[#6D716C]" />
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </AppShell>
  );
}
